import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { defineComponent } from 'vue';
import constants from '../shared/constants';
import { escapeHtml, normalizeStringValue, parseLocaleNumber } from '../shared/functions';
import type {
    APIVolunteerGroupItem,
    VolunteerGroupsMapItem,
    VolunteerGroupsMapMarker,
    VolunteerGroupsMapMarkerEntry,
    VolunteerGroupsMapResponse,
} from '../shared/types';

const defaultMarkerIcon = L.icon({
    iconRetinaUrl: constants.assets.leaflet.markerIcon2xUrl,
    iconUrl: constants.assets.leaflet.markerIconUrl,
    shadowUrl: constants.assets.leaflet.markerShadowUrl,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    tooltipAnchor: [16, -28],
    shadowSize: [41, 41],
});

export default defineComponent({
    data() {
        return {
            categories: [] as string[],
            clusterGroup: null as L.MarkerClusterGroup | null,
            constants,
            dataElement: null as HTMLElement | null,
            emptyResultsText: '',
            filtersPanelElement: null as HTMLElement | null,
            defaultLatitude: 52.0842715,
            defaultLongitude: 5.0124521,
            defaultZoom: 10,
            filtersOpen: false,
            handleResize: null as (() => void) | null,
            instanceId: 'default',
            loadErrorText: '',
            map: null as L.Map | null,
            markers: [] as VolunteerGroupsMapMarkerEntry[],
            searchPath: '',
            selectedCategories: [] as string[],
            warningElement: null as HTMLElement | null,
            websiteId: '',
            pageSize: '',
            canvasElement: null as HTMLElement | null,
        };
    },
    async mounted() {
        this.initializeElements();
        this.readDataset();
        this.selectedCategories = this.getActiveCategoryQueryValues();
        this.filtersOpen = this.hasActiveFilterQueryParams();
        this.initializeMap();
        await this.loadMarkers();
        this.syncFiltersPanelHeight();

        this.handleResize = () => {
            this.refreshMapLayout();
            this.syncFiltersPanelHeight();
        };

        window.addEventListener('resize', this.handleResize);

        window.setTimeout(() => {
            this.refreshMapLayout();
        }, 0);
    },
    beforeUnmount() {
        if (this.handleResize) {
            window.removeEventListener('resize', this.handleResize);
        }

        this.map?.remove();
    },
    methods: {
        getRootElement(): HTMLElement | null {
            return this.$el as HTMLElement | null;
        },
        getDataElement(): HTMLElement | null {
            return this.getRootElement()?.querySelector(constants.targets.volunteerGroupsMap.element) ?? null;
        },
        initializeElements(): void {
            this.dataElement = this.getDataElement();

            if (!this.dataElement) return;

            this.canvasElement = this.dataElement.querySelector(constants.targets.volunteerGroupsMap.canvas);
            this.warningElement = this.dataElement.querySelector(constants.targets.volunteerGroupsMap.warning);
            this.filtersPanelElement = this.dataElement.querySelector(constants.targets.volunteerGroupsMap.filtersPanel);
        },
        readDataset(): void {
            if (!this.dataElement) return;

            this.instanceId = this.dataElement.dataset.instanceId ?? this.instanceId;
            this.searchPath = this.dataElement.dataset.searchPath ?? '';
            this.pageSize = this.dataElement.dataset[constants.queryParams.pageSize] ?? '';
            this.websiteId = this.dataElement.dataset[constants.queryParams.websiteId] ?? '';
            this.emptyResultsText = this.dataElement.dataset.emptyResultsText ?? '';
            this.loadErrorText = this.dataElement.dataset.loadErrorText ?? '';
            this.defaultZoom = parseLocaleNumber(this.dataElement.dataset.defaultZoom ?? `${this.defaultZoom}`, this.defaultZoom);
            this.defaultLatitude = parseLocaleNumber(this.dataElement.dataset.latitude ?? `${this.defaultLatitude}`, this.defaultLatitude);
            this.defaultLongitude = parseLocaleNumber(this.dataElement.dataset.longitude ?? `${this.defaultLongitude}`, this.defaultLongitude);
        },
        getCategoryInputId(category: string, index: number): string {
            return `map-category-${this.instanceId}-${index}-${category.toLowerCase().replaceAll(/[^a-z0-9]+/g, '-')}`;
        },
        refreshMapLayout(): void {
            const map = this.map as L.Map | null;

            if (!map) return;

            map.invalidateSize(false);
        },
        syncFiltersPanelHeight(): void {
            const filtersPanelElement = this.filtersPanelElement;

            if (!filtersPanelElement) return;

            filtersPanelElement.style.maxHeight = this.filtersOpen ? `${filtersPanelElement.scrollHeight}px` : '0';
        },
        toggleFilters(): void {
            this.filtersOpen = !this.filtersOpen;

            this.$nextTick(() => {
                this.syncFiltersPanelHeight();
            });
        },
        normalizeFilterValue(value: string): string {
            return normalizeStringValue(value);
        },
        getActiveCategoryQueryValues(): string[] {
            const categoryQueryValue = new URLSearchParams(window.location.search).get(constants.queryParams.categories);

            if (!categoryQueryValue) {
                return [];
            }

            return categoryQueryValue
                .split(',')
                .map((value) => normalizeStringValue(value))
                .filter((value, index, values) => value.length > 0 && values.indexOf(value) === index);
        },
        hasActiveFilterQueryParams(): boolean {
            return this.getActiveCategoryQueryValues().length > 0;
        },
        getSearchUrl(): string {
            if (!this.searchPath) return '';

            const urlParams = new URLSearchParams();

            if (this.pageSize) urlParams.set(constants.queryParams.pageSize, this.pageSize);
            if (this.websiteId) urlParams.set(constants.queryParams.websiteId, this.websiteId);

            const queryString = urlParams.toString();

            return queryString ? `${this.searchPath}?${queryString}` : this.searchPath;
        },
        initializeMap(): void {
            const canvasElement = this.canvasElement;

            if (!canvasElement) return;

            const map = L.map(canvasElement, {
                scrollWheelZoom: true,
                zoomControl: true,
            }).setView([this.defaultLatitude, this.defaultLongitude], this.defaultZoom);
            this.map = map;

            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 19,
            }).addTo(map);

            const clusterGroup = L.markerClusterGroup({
                showCoverageOnHover: false,
                spiderfyOnMaxZoom: true,
                zoomToBoundsOnClick: true,
            });
            this.clusterGroup = clusterGroup;

            map.on('zoomstart', () => {
                map.closePopup();
            });

            map.on('zoomend', () => {
                this.renderMarkers();
            });

            clusterGroup.addTo(map);
        },
        async loadMarkers(): Promise<void> {
            const requestUrl = this.getSearchUrl();

            if (!requestUrl) return;

            try {
                const response = await axios.post<VolunteerGroupsMapResponse>(requestUrl);
                const items = this.normalizeItems(response.data.items ?? []);

                this.markers = items.map((item) => {
                    const marker = L.marker([item.lat, item.lon], {
                        icon: defaultMarkerIcon,
                    }) as VolunteerGroupsMapMarker;
                    const category = item.category?.trim() ?? '';

                    marker.volunteerGroupCategory = category;
                    marker.volunteerGroupItem = item;
                    marker.on('click', () => {
                        this.openPopupForItems([item], marker.getLatLng(), true);
                    });

                    return {
                        category,
                        item,
                        marker,
                    };
                });

                this.categories = this.getCategories(items);
                this.renderMarkers();
                this.$nextTick(() => {
                    this.syncFiltersPanelHeight();
                });
            } catch {
                this.setWarningText(this.loadErrorText);
                this.setWarningVisibility(true);
            }
        },
        normalizeItems(items: APIVolunteerGroupItem[]): VolunteerGroupsMapItem[] {
            return items.reduce<VolunteerGroupsMapItem[]>((accumulator, item) => {
                const latitude = parseLocaleNumber(`${item.lat ?? ''}`, Number.NaN);
                const longitude = parseLocaleNumber(`${item.lon ?? ''}`, Number.NaN);

                if (Number.isFinite(latitude) === false || Number.isFinite(longitude) === false) {
                    return accumulator;
                }

                if (latitude === 0 && longitude === 0) {
                    return accumulator;
                }

                accumulator.push({
                    ...item,
                    lat: latitude,
                    lon: longitude,
                });

                return accumulator;
            }, []);
        },
        getCategories(items: VolunteerGroupsMapItem[]): string[] {
            return Array.from(new Set(items.map((item) => item.category?.trim() ?? '').filter((category) => category.length > 0)))
                .sort((left, right) => left.localeCompare(right, 'nl'));
        },
        setWarningText(text: string): void {
            const warningElement = this.warningElement;

            if (!warningElement) return;

            const warningTextElement = warningElement.querySelector('p') ?? warningElement;
            warningTextElement.textContent = text;
        },
        setWarningVisibility(isVisible: boolean): void {
            const warningElement = this.warningElement;

            if (!warningElement) return;

            warningElement.classList.toggle(constants.classes.hidden, !isVisible);
        },
        getSelectedCategories(): string[] {
            return this.selectedCategories
                .map((value) => normalizeStringValue(value))
                .filter((value, index, values) => value.length > 0 && values.indexOf(value) === index);
        },
        getQueryString(): string {
            const urlParams = new URLSearchParams(window.location.search);
            const selectedCategories = this.getSelectedCategories();

            urlParams.delete(constants.queryParams.categories);
            urlParams.delete(constants.queryParams.page);

            if (selectedCategories.length > 0) {
                urlParams.set(constants.queryParams.categories, selectedCategories.join(','));
            }

            return urlParams.toString();
        },
        updateBrowserUrl(): void {
            const queryString = this.getQueryString();
            const nextUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;

            history.replaceState({}, '', nextUrl);
        },
        renderMarkers(): void {
            const clusterGroup = this.clusterGroup as L.MarkerClusterGroup | null;
            const map = this.map as L.Map | null;

            if (!clusterGroup || !map) return;

            const selectedCategories = this.getSelectedCategories();
            const filteredMarkers = this.markers.filter(({ category }) => {
                if (selectedCategories.length === 0) {
                    return true;
                }

                return selectedCategories.some((selectedCategory) => category.toLowerCase().includes(selectedCategory));
            });

            clusterGroup.clearLayers();
            map.closePopup();

            if (filteredMarkers.length === 0) {
                this.setWarningText(this.emptyResultsText);
                this.setWarningVisibility(true);
                map.setView([this.defaultLatitude, this.defaultLongitude], this.defaultZoom);
                return;
            }

            filteredMarkers.forEach(({ marker }) => {
                clusterGroup.addLayer(marker as unknown as L.Layer);
            });

            this.setWarningVisibility(false);
        },
        updateFilter(): void {
            this.updateBrowserUrl();
            this.renderMarkers();
            this.syncFiltersPanelHeight();
        },
        openPopupForItems(items: VolunteerGroupsMapItem[], latLng: L.LatLngExpression, centerMap = false): void {
            const map = this.map as L.Map | null;

            if (!map || items.length === 0) return;

            if (centerMap) {
                map.panTo(latLng);
            }

            L.popup({
                autoPan: false,
                className: 'map__popup',
                maxWidth: 320,
                offset: L.point(0, -34),
            })
                .setLatLng(latLng)
                .setContent(this.renderPopupContent(items))
                .openOn(map);
        },
        renderPopupContent(items: VolunteerGroupsMapItem[]): string {
            const popupItems = items.map((item) => {
                const title = escapeHtml(item.title);
                const previewText = item.previewText ? `<p class="map__popup-text mb-0">${escapeHtml(item.previewText)}</p>` : '';
                const detailUrl = item.detailUrl ? escapeHtml(item.detailUrl) : '';
                const titleMarkup = detailUrl
                    ? `<a class="map__popup-title title is-6" href="${detailUrl}">${title}</a>`
                    : `<p class="map__popup-title title is-6">${title}</p>`;

                return `<div class="map__popup-item content">${titleMarkup}${previewText}</div>`;
            }).join('');

            return popupItems;
        },
    },
});
